import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  AreaChart, Area,
} from 'recharts';
import { Cloud, Factory, Lightning, Truck, Leaf, ShieldCheck, Info } from '@phosphor-icons/react';
import { getEmissionInventory } from '../api';
import { useTranslation } from '../i18n/LanguageContext';

const SCOPE_COLORS = { scope1: '#ef4444', scope2: '#facc15', scope3: '#60a5fa' };
const SCOPE_LABELS = { scope1: 'Scope 1 — Direct', scope2: 'Scope 2 — Energy Indirect', scope3: 'Scope 3 — Value Chain' };

const GAS_COLORS = {
  CO2: '#94a3b8', CH4: '#facc15', N2O: '#c084fc', HFCs: '#2dd4bf', PFCs: '#f97316', SF6: '#ef4444', NF3: '#60a5fa',
};

function fmtT(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  if (n >= 1) return n.toFixed(1);
  if (n >= 0.01) return n.toFixed(2);
  return n.toFixed(4);
}

const TABS = ['overview', 'scope1', 'scope2', 'scope3', 'gases', 'boundary'];

function ScopeTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body">
      <p className="font-semibold text-text-primary mb-1">{d.name}</p>
      <p className="text-text-secondary">{fmtT(d.value)} tCO2e ({d.pct}%)</p>
    </div>
  );
}

function SourceTable({ sources, scopeLabel }) {
  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden" data-testid={`emission-source-table-${scopeLabel}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="border-b border-border bg-background">
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">ID</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Category</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Description</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Gases</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">tCO2e</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s, idx) => (
              <tr key={idx} className="border-b border-border/40 hover:bg-white/[0.02]">
                <td className="py-2.5 px-3 font-mono text-xs text-text-secondary">{s.source_id}</td>
                <td className="py-2.5 px-3 text-xs font-medium text-text-primary">{s.category}</td>
                <td className="py-2.5 px-3 text-xs text-text-secondary max-w-xs">{s.description}</td>
                <td className="py-2.5 px-3">
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(s.emissions || {}).map(g => (
                      <span key={g} className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: (GAS_COLORS[g] || '#666') + '20', color: GAS_COLORS[g] || '#666' }}>
                        {g}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-2.5 px-3 text-right font-mono text-xs font-semibold text-text-primary">{fmtT(s.total_co2e)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border">
              <td colSpan={4} className="py-2.5 px-3 text-xs font-semibold text-text-primary uppercase">{scopeLabel} Total</td>
              <td className="py-2.5 px-3 text-right font-mono text-sm font-bold text-text-primary">
                {fmtT(sources.reduce((a, s) => a + s.total_co2e, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function OverviewTab({ data }) {
  const s = data.summary;
  const pieData = [
    { name: 'Scope 1 — Direct', value: s.scope1_co2e, pct: s.scope1_pct, color: SCOPE_COLORS.scope1 },
    { name: 'Scope 2 — Energy', value: s.scope2_co2e, pct: s.scope2_pct, color: SCOPE_COLORS.scope2 },
    { name: 'Scope 3 — Value Chain', value: s.scope3_co2e, pct: s.scope3_pct, color: SCOPE_COLORS.scope3 },
  ];

  const trendData = data.trend || [];
  const baseYear = data.boundary?.base_year_emissions_co2e;
  const yoyChange = baseYear ? ((s.total_co2e - baseYear) / baseYear * 100).toFixed(1) : null;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="emission-overview">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <Cloud size={18} className="text-text-secondary mx-auto mb-1" weight="duotone" />
          <p className="font-heading text-2xl font-bold text-text-primary">{fmtT(s.total_co2e)}</p>
          <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Total tCO2e/yr</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <Factory size={18} className="mx-auto mb-1" style={{ color: SCOPE_COLORS.scope1 }} weight="duotone" />
          <p className="font-heading text-2xl font-bold" style={{ color: SCOPE_COLORS.scope1 }}>{fmtT(s.scope1_co2e)}</p>
          <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Scope 1 ({s.scope1_pct}%)</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <Lightning size={18} className="mx-auto mb-1" style={{ color: SCOPE_COLORS.scope2 }} weight="duotone" />
          <p className="font-heading text-2xl font-bold" style={{ color: SCOPE_COLORS.scope2 }}>{fmtT(s.scope2_co2e)}</p>
          <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Scope 2 ({s.scope2_pct}%)</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <Truck size={18} className="mx-auto mb-1" style={{ color: SCOPE_COLORS.scope3 }} weight="duotone" />
          <p className="font-heading text-2xl font-bold" style={{ color: SCOPE_COLORS.scope3 }}>{fmtT(s.scope3_co2e)}</p>
          <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Scope 3 ({s.scope3_pct}%)</p>
        </div>
      </div>

      {/* Reduction target strip */}
      {baseYear && (
        <div className="bg-surface border border-border rounded-lg p-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-body font-semibold text-text-primary">Progress vs Base Year ({data.boundary?.base_year})</p>
            <p className="text-xs font-body text-text-secondary mt-0.5">{data.boundary?.target}</p>
          </div>
          <div className="flex items-center gap-4 text-center">
            <div>
              <p className="font-heading text-lg font-bold text-text-secondary">{fmtT(baseYear)}</p>
              <p className="text-[10px] font-body text-text-secondary">Base Year</p>
            </div>
            <div>
              <p className="font-heading text-lg font-bold text-text-primary">{fmtT(s.total_co2e)}</p>
              <p className="text-[10px] font-body text-text-secondary">Current</p>
            </div>
            <div>
              <p className={`font-heading text-lg font-bold ${parseFloat(yoyChange) <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {yoyChange > 0 ? '+' : ''}{yoyChange}%
              </p>
              <p className="text-[10px] font-body text-text-secondary">vs Base</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Scope Pie */}
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">Emissions by Scope</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                  {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<ScopeTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {pieData.map(p => (
              <span key={p.name} className="flex items-center gap-1.5 text-[10px] font-body text-text-secondary">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} /> {p.name.split('—')[0].trim()}
              </span>
            ))}
          </div>
        </div>

        {/* Gas Breakdown Bar */}
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">Emissions by Gas (tCO2e)</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(s.gas_totals).map(([g, v]) => ({ gas: g, co2e: v.co2e }))} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="gas" tick={{ fill: '#A3A380', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A3A380', fontSize: 11 }} tickFormatter={v => fmtT(v)} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body">
                      <p className="font-semibold text-text-primary">{d.gas}: {fmtT(d.co2e)} tCO2e</p>
                    </div>
                  );
                }} />
                <Bar dataKey="co2e" radius={[4, 4, 0, 0]} barSize={28}>
                  {Object.keys(s.gas_totals).map((g, idx) => (
                    <Cell key={idx} fill={GAS_COLORS[g] || '#6b7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">Monthly Emission Trend (tCO2e)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trendData} margin={{ left: 0, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis dataKey="month" tick={{ fill: '#A3A380', fontSize: 11 }} />
              <YAxis tick={{ fill: '#A3A380', fontSize: 11 }} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body">
                    <p className="font-semibold text-text-primary mb-1">{label}</p>
                    {payload.map(p => (
                      <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value} tCO2e</p>
                    ))}
                  </div>
                );
              }} />
              <Legend />
              <Area type="monotone" dataKey="scope1" name="Scope 1" stackId="1" fill={SCOPE_COLORS.scope1} stroke={SCOPE_COLORS.scope1} fillOpacity={0.6} />
              <Area type="monotone" dataKey="scope2" name="Scope 2" stackId="1" fill={SCOPE_COLORS.scope2} stroke={SCOPE_COLORS.scope2} fillOpacity={0.6} />
              <Area type="monotone" dataKey="scope3" name="Scope 3" stackId="1" fill={SCOPE_COLORS.scope3} stroke={SCOPE_COLORS.scope3} fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function GasesTab({ data }) {
  const gases = data.gases || [];
  const totals = data.summary?.gas_totals || {};

  return (
    <div className="space-y-5 animate-fade-in" data-testid="emission-gases">
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Info size={14} className="text-blue-400" weight="fill" />
          <p className="text-xs font-body text-text-secondary">
            The GHG Protocol requires reporting of all seven Kyoto Protocol greenhouse gases. GWP values shown are IPCC AR5 (used for GHG Protocol) and AR6 (latest science).
          </p>
        </div>
      </div>

      {/* Gas Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {gases.map(g => {
          const t = totals[g.gas_id];
          const co2e = t?.co2e || 0;
          const tonnes = t?.tonnes || 0;
          const totalCo2e = data.summary?.total_co2e || 1;
          const pct = ((co2e / totalCo2e) * 100).toFixed(2);
          const color = GAS_COLORS[g.gas_id] || '#6b7280';

          return (
            <div key={g.gas_id} className="bg-surface border border-border rounded-lg p-4 hover:border-tertiary/40 transition-all" data-testid={`emission-gas-${g.gas_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '18' }}>
                    <span className="font-heading text-xs font-bold" style={{ color }}>{g.formula}</span>
                  </div>
                  <span className="text-sm font-body font-semibold text-text-primary">{g.name}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-background rounded-md p-2 text-center">
                  <p className="font-heading text-base font-bold text-text-primary">{fmtT(tonnes)}</p>
                  <p className="text-[9px] font-body text-text-secondary">tonnes (direct)</p>
                </div>
                <div className="bg-background rounded-md p-2 text-center">
                  <p className="font-heading text-base font-bold" style={{ color }}>{fmtT(co2e)}</p>
                  <p className="text-[9px] font-body text-text-secondary">tCO2e</p>
                </div>
              </div>
              {/* Contribution bar */}
              <div className="mb-2">
                <div className="flex items-center justify-between text-[10px] font-body mb-1">
                  <span className="text-text-secondary">Contribution</span>
                  <span className="font-semibold" style={{ color }}>{pct}%</span>
                </div>
                <div className="h-1.5 bg-background rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(parseFloat(pct), 100)}%`, backgroundColor: color }} />
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] font-body text-text-secondary">
                <span>GWP AR5: {g.gwp_ar5?.toLocaleString()}</span>
                <span>GWP AR6: {g.gwp_ar6?.toLocaleString()}</span>
              </div>
              <p className="text-[10px] font-body text-text-secondary mt-2 leading-relaxed">{g.description}</p>
            </div>
          );
        })}
      </div>

      {/* Gas summary table */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm font-body" data-testid="emission-gas-table">
          <thead>
            <tr className="border-b border-border bg-background">
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Gas</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Formula</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Tonnes (direct)</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">GWP (AR5)</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">tCO2e</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">% of Total</th>
            </tr>
          </thead>
          <tbody>
            {gases.map(g => {
              const t = totals[g.gas_id];
              const co2e = t?.co2e || 0;
              const totalCo2e = data.summary?.total_co2e || 1;
              return (
                <tr key={g.gas_id} className="border-b border-border/40 hover:bg-white/[0.02]">
                  <td className="py-2.5 px-3 text-xs font-medium text-text-primary">{g.name}</td>
                  <td className="py-2.5 px-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: (GAS_COLORS[g.gas_id] || '#666') + '20', color: GAS_COLORS[g.gas_id] }}>
                      {g.formula}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs text-text-primary">{fmtT(t?.tonnes || 0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs text-text-secondary">{g.gwp_ar5?.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs font-semibold text-text-primary">{fmtT(co2e)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs" style={{ color: GAS_COLORS[g.gas_id] }}>{((co2e / totalCo2e) * 100).toFixed(2)}%</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border">
              <td colSpan={4} className="py-2.5 px-3 text-xs font-semibold text-text-primary uppercase">Total</td>
              <td className="py-2.5 px-3 text-right font-mono text-sm font-bold text-text-primary">{fmtT(data.summary?.total_co2e || 0)}</td>
              <td className="py-2.5 px-3 text-right font-mono text-xs font-bold text-text-primary">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function BoundaryTab({ data }) {
  const b = data.boundary || {};
  return (
    <div className="space-y-5 animate-fade-in" data-testid="emission-boundary">
      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">Inventory Boundary & Methodology</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Organizational Boundary', value: b.organizational_boundary },
            { label: 'Consolidation Approach', value: b.consolidation_approach },
            { label: 'Reporting Period', value: b.reporting_period },
            { label: 'Base Year', value: b.base_year },
            { label: 'Base Year Emissions', value: `${fmtT(b.base_year_emissions_co2e || 0)} tCO2e` },
            { label: 'Reduction Target', value: b.target },
            { label: 'Verification Status', value: b.verification_status },
          ].map(item => (
            <div key={item.label} className="bg-background rounded-lg p-3">
              <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary mb-1">{item.label}</p>
              <p className="text-sm font-body text-text-primary">{item.value || '—'}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">Reporting Standards</h3>
        <div className="flex flex-wrap gap-2">
          {(b.standards || []).map((std, idx) => (
            <span key={idx} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-body font-medium border border-emerald-500/20">
              <ShieldCheck size={12} weight="fill" /> {std}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">Scope 3 Categories Included</h3>
          <div className="flex flex-wrap gap-2">
            {(b.scope3_categories_included || []).map(cat => (
              <span key={cat} className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-body font-medium border border-blue-500/20">
                Category {cat}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">Excluded Categories</h3>
          <p className="text-xs font-body text-text-secondary">{b.scope3_categories_excluded_reason || '—'}</p>
        </div>
      </div>
    </div>
  );
}

export default function EmissionInventory({ trackFeature }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleTabChange = (tabId) => {
    setTab(tabId);
    if (trackFeature) trackFeature('emissions', `emission_tab_${tabId}`, tabId);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEmissionInventory();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load emission data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="emission-loading">
        <div className="h-12 bg-surface border border-border rounded-lg w-full" />
        <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-surface border border-border rounded-lg" />)}</div>
        <div className="h-64 bg-surface border border-border rounded-lg" />
      </div>
    );
  }

  const renderTab = () => {
    if (!data) return null;
    switch (tab) {
      case 'overview': return <OverviewTab data={data} />;
      case 'scope1': return <SourceTable sources={data.scope1 || []} scopeLabel="scope1" />;
      case 'scope2': return <SourceTable sources={data.scope2 || []} scopeLabel="scope2" />;
      case 'scope3': return <SourceTable sources={data.scope3 || []} scopeLabel="scope3" />;
      case 'gases': return <GasesTab data={data} />;
      case 'boundary': return <BoundaryTab data={data} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-5" data-testid="emission-inventory">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-text-primary">{t('emissions.title')}</h1>
        <p className="text-sm font-body text-text-secondary mt-1">
          {t('emissions.subtitle')}
        </p>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1 bg-surface border border-border rounded-lg p-1" data-testid="emission-tabs">
        {TABS.map(id => (
          <button key={id} data-testid={`emission-tab-${id}`} onClick={() => handleTabChange(id)}
            className={`whitespace-nowrap px-4 py-2 rounded-md text-sm font-body font-medium transition-all duration-200 ${
              tab === id ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
            }`}>
            {t(`emissions.tabs.${id}`)}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  );
}
